/**
 * Cart Service for Marketplace
 * Manages cart state and operations using localStorage
 */

const CART_STORAGE_KEY = 'marketplace_cart';

/**
 * Get cart items from localStorage
 * @returns {Array} Array of cart items
 */
export const getCartItems = () => {
  try {
    const cartData = localStorage.getItem(CART_STORAGE_KEY);
    return cartData ? JSON.parse(cartData) : [];
  } catch (error) {
    console.error('Error getting cart items:', error);
    return [];
  }
};

/**
 * Save cart items to localStorage
 * @param {Array} items - Array of cart items
 */
export const saveCartItems = (items) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    return true;
  } catch (error) {
    console.error('Error saving cart items:', error);
    return false;
  }
};

/**
 * Add item to cart
 * @param {Object} product - Product object
 * @param {Number} quantity - Quantity to add
 * @param {String} selectedDenomination - Selected denomination value
 * @returns {Object} Updated cart items
 */
export const addToCart = (product, quantity = 1, selectedDenomination = null) => {
  try {
    const cartItems = getCartItems();
    // Create unique key combining product ID and denomination
    const cartItemKey = `${product._id}_${selectedDenomination}`;
    const existingItemIndex = cartItems.findIndex(
      (item) => item.productId === product._id && item.selectedDenomination === selectedDenomination
    );

    if (existingItemIndex >= 0) {
      // Update quantity if item with same denomination already exists
      cartItems[existingItemIndex].quantity += quantity;
    } else {
      // Add new item
      cartItems.push({
        cartItemKey,
        productId: product._id,
        productCode: product.productCode,
        productName: product.productName,
        provider: product.provider,
        brandName: product.brandName,
        imageUrl: product.imageUrl,
        denominations: product.denominations,
        selectedDenomination: selectedDenomination,
        currencyCode: product.currencyCode,
        customerDiscountPercentage: product.customerDiscountPercentage,
        quantity: quantity,
        addedAt: new Date().toISOString()
      });
    }

    saveCartItems(cartItems);
    return {
      success: true,
      cartItems,
      totalItems: getTotalCartItems(cartItems),
      uniqueProducts: getUniqueProductsCount(cartItems)
    };
  } catch (error) {
    console.error('Error adding to cart:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove item from cart
 * @param {String} cartItemKey - Cart item key (productId_denomination) to remove
 * @returns {Object} Updated cart items
 */
export const removeFromCart = (cartItemKey) => {
  try {
    const cartItems = getCartItems();
    const updatedItems = cartItems.filter((item) => item.cartItemKey !== cartItemKey);
    saveCartItems(updatedItems);
    return {
      success: true,
      cartItems: updatedItems,
      totalItems: getTotalCartItems(updatedItems),
      uniqueProducts: getUniqueProductsCount(updatedItems)
    };
  } catch (error) {
    console.error('Error removing from cart:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update item quantity in cart
 * @param {String} cartItemKey - Cart item key (productId_denomination)
 * @param {Number} quantity - New quantity
 * @returns {Object} Updated cart items
 */
export const updateCartItemQuantity = (cartItemKey, quantity) => {
  try {
    const cartItems = getCartItems();
    const itemIndex = cartItems.findIndex((item) => item.cartItemKey === cartItemKey);

    if (itemIndex >= 0) {
      if (quantity <= 0) {
        // Remove item if quantity is 0 or less
        return removeFromCart(cartItemKey);
      } else {
        cartItems[itemIndex].quantity = quantity;
        saveCartItems(cartItems);
        return {
          success: true,
          cartItems,
          totalItems: getTotalCartItems(cartItems),
          uniqueProducts: getUniqueProductsCount(cartItems)
        };
      }
    }

    return { success: false, error: 'Item not found in cart' };
  } catch (error) {
    console.error('Error updating cart item quantity:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Clear all items from cart
 * @returns {Object} Result
 */
export const clearCart = () => {
  try {
    localStorage.removeItem(CART_STORAGE_KEY);
    return { success: true, cartItems: [], totalItems: 0 };
  } catch (error) {
    console.error('Error clearing cart:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get total number of items in cart
 * @param {Array} items - Optional cart items array (if not provided, fetches from storage)
 * @returns {Number} Total number of items
 */
export const getTotalCartItems = (items = null) => {
  try {
    const cartItems = items || getCartItems();
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  } catch (error) {
    console.error('Error getting total cart items:', error);
    return 0;
  }
};

/**
 * Get number of unique products in cart
 * @param {Array} items - Optional cart items array (if not provided, fetches from storage)
 * @returns {Number} Number of unique products
 */
export const getUniqueProductsCount = (items = null) => {
  try {
    const cartItems = items || getCartItems();
    return cartItems.length;
  } catch (error) {
    console.error('Error getting unique products count:', error);
    return 0;
  }
};

/**
 * Get cart item by cart item key
 * @param {String} cartItemKey - Cart item key (productId_denomination)
 * @returns {Object|null} Cart item or null
 */
export const getCartItem = (cartItemKey) => {
  try {
    const cartItems = getCartItems();
    return cartItems.find((item) => item.cartItemKey === cartItemKey) || null;
  } catch (error) {
    console.error('Error getting cart item:', error);
    return null;
  }
};

/**
 * Check if product with specific denomination is in cart
 * @param {String} productId - Product ID
 * @param {String} denomination - Denomination value (optional)
 * @returns {Boolean} True if product/denomination combo is in cart
 */
export const isInCart = (productId, denomination = null) => {
  try {
    const cartItems = getCartItems();
    if (denomination) {
      return cartItems.some(
        (item) => item.productId === productId && item.selectedDenomination === denomination
      );
    }
    return cartItems.some((item) => item.productId === productId);
  } catch (error) {
    console.error('Error checking if product is in cart:', error);
    return false;
  }
};

/**
 * Subscribe to cart changes
 * Note: This is a simple implementation. For more complex apps, consider using Context API or Redux
 */
export const cartService = {
  getCartItems,
  saveCartItems,
  addToCart,
  removeFromCart,
  updateCartItemQuantity,
  clearCart,
  getTotalCartItems,
  getUniqueProductsCount,
  getCartItem,
  isInCart
};

export default cartService;
